var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { contrastText } from "./color";
import { FORMATION_DRAG_TYPE, } from "./types";
var ROW_BG = "rgba(15, 23, 42, 0.04)";
var ROW_BG_HOVER = "rgba(15, 23, 42, 0.07)";
var TEXT_PRIMARY = "#0f172a";
var TEXT_SECONDARY = "rgba(15, 23, 42, 0.65)";
var EMPTY_TEXT = "Bench is empty";
var DEFAULT_HEADING = "Bench";
var POSITION_ORDER = {
    GK: 0,
    SW: 1,
    LWB: 2,
    LB: 3,
    LCB: 4,
    CB: 5,
    RCB: 6,
    RB: 7,
    RWB: 8,
    CDM: 10,
    LM: 12,
    LCM: 13,
    CM: 14,
    RCM: 15,
    RM: 16,
    CAM: 20,
    LAM: 21,
    RAM: 22,
    LW: 24,
    RW: 25,
    CF: 28,
    ST: 30,
};
var positionOrder = function (code) {
    return code === null ? 999 : POSITION_ORDER[code];
};
var sortByPosition = function (players) {
    return __spreadArray([], __read(players), false).sort(function (a, b) {
        var pa = positionOrder(a.primaryPosition);
        var pb = positionOrder(b.primaryPosition);
        if (pa !== pb)
            return pa - pb;
        return a.lastName.localeCompare(b.lastName);
    });
};
var BenchPlayerItem = function (_a) {
    var _b, _c;
    var player = _a.player, primaryColour = _a.primaryColour, secondaryColour = _a.secondaryColour, _d = _a.isSelected, isSelected = _d === void 0 ? false : _d, onClick = _a.onClick, onSwapWithPitchPlayer = _a.onSwapWithPitchPlayer;
    var _e = __read(useDrag(function () { return ({
        type: FORMATION_DRAG_TYPE,
        item: { source: "bench", player: player },
        collect: function (monitor) { return ({ isDragging: monitor.isDragging() }); },
    }); }, [player]), 2), isDragging = _e[0].isDragging, dragRef = _e[1];
    var _f = __read(useDrop(function () { return ({
        accept: FORMATION_DRAG_TYPE,
        canDrop: function (item) { return item.source === "pitch"; },
        drop: function (item) {
            if (item.source === "pitch" && onSwapWithPitchPlayer) {
                onSwapWithPitchPlayer(item.slotId, player);
            }
        },
        collect: function (monitor) { return ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }); },
    }); }, [player, onSwapWithPitchPlayer]), 2), _g = _f[0], isOver = _g.isOver, canDrop = _g.canDrop, dropRef = _f[1];
    var setRefs = function (node) {
        dragRef(node);
        dropRef(node);
    };
    var numberTextColour = contrastText(primaryColour);
    var isDropTarget = isOver && canDrop;
    return (_jsxs(Box, { ref: setRefs, "data-testid": "bench-player-".concat(player.id), role: "button", "aria-pressed": isSelected, "aria-label": "Bench: #".concat((_b = player.squadNumber) !== null && _b !== void 0 ? _b : "?", " ").concat(player.lastName).concat(player.primaryPosition ? " (".concat(player.primaryPosition, ")") : ""), onClick: function (e) {
            e.stopPropagation();
            onClick === null || onClick === void 0 ? void 0 : onClick(player);
        }, sx: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 0,
            pr: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: ROW_BG,
            borderLeft: "3px solid ".concat(primaryColour),
            color: TEXT_PRIMARY,
            cursor: "grab",
            opacity: isDragging ? 0.3 : 1,
            userSelect: "none",
            outline: isSelected
                ? "2px solid ".concat(primaryColour)
                : isDropTarget
                    ? "2px dashed ".concat(primaryColour)
                    : "none",
            outlineOffset: 2,
            transition: "background-color 0.1s ease, outline 0.1s ease",
            "&:hover": {
                backgroundColor: ROW_BG_HOVER,
            },
        }, children: [_jsx(Box, { sx: {
                    ml: 1,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: primaryColour,
                    border: "1.5px solid rgba(15, 23, 42, 0.55)",
                    color: numberTextColour,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    flexShrink: 0,
                }, children: (_c = player.squadNumber) !== null && _c !== void 0 ? _c : "-" }), _jsx(Typography, { noWrap: true, sx: {
                    flex: 1,
                    fontWeight: 500,
                    fontSize: "0.9rem",
                }, children: player.lastName }), player.primaryPosition && (_jsx(Box, { sx: {
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.75,
                    backgroundColor: "rgba(15, 23, 42, 0.06)",
                    border: "1px solid rgba(15, 23, 42, 0.12)",
                    color: TEXT_SECONDARY,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    lineHeight: 1,
                    flexShrink: 0,
                }, children: player.primaryPosition }))] }));
};
export var Bench = function (_a) {
    var players = _a.players, primaryColour = _a.primaryColour, secondaryColour = _a.secondaryColour, onDrop = _a.onDrop, onSwapBenchPlayer = _a.onSwapBenchPlayer, _b = _a.heading, heading = _b === void 0 ? DEFAULT_HEADING : _b, _c = _a.selectedPlayerId, selectedPlayerId = _c === void 0 ? null : _c, onClickPlayer = _a.onClickPlayer, onClickArea = _a.onClickArea;
    var sorted = useMemo(function () { return sortByPosition(players); }, [players]);
    var _d = __read(useDrop(function () { return ({
        accept: FORMATION_DRAG_TYPE,
        canDrop: function (item) { return item.source !== "bench"; },
        drop: function (item, monitor) {
            // If a BenchPlayerItem already handled the drop (swap), skip the
            // outer remove-to-bench behaviour.
            if (monitor.didDrop())
                return;
            onDrop(item);
        },
        collect: function (monitor) { return ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }); },
    }); }, [onDrop]), 2), _e = _d[0], isOver = _e.isOver, canDrop = _e.canDrop, dropRef = _d[1];
    var isHighlighted = isOver && canDrop;
    return (_jsxs(Box, { ref: dropRef, "data-testid": "formation-bench", onClick: function () { return onClickArea === null || onClickArea === void 0 ? void 0 : onClickArea(); }, sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: "transparent",
            p: 1.5,
            gap: 1,
            outline: isHighlighted ? "2px dashed ".concat(primaryColour) : "none",
            outlineOffset: "-4px",
            transition: "outline 0.1s ease",
            width: "100%",
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            cursor: onClickArea ? "pointer" : "default",
        }, children: [_jsx(Typography, { component: "h1", sx: {
                    color: TEXT_PRIMARY,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    lineHeight: 1.2,
                    mb: 0.5,
                    flexShrink: 0,
                }, children: heading }), sorted.length === 0 ? (_jsx(Typography, { sx: {
                    color: TEXT_SECONDARY,
                    fontStyle: "italic",
                    fontSize: "0.85rem",
                }, children: EMPTY_TEXT })) : (_jsx(Stack, { spacing: 0.75, sx: {
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    // Slim, theme-aware scrollbar so it doesn't dominate the column.
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(15,23,42,0.25) transparent",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(15,23,42,0.25)",
                        borderRadius: 3,
                    },
                    pr: 0.5,
                }, children: sorted.map(function (p) { return (_jsx(BenchPlayerItem, { player: p, primaryColour: primaryColour, secondaryColour: secondaryColour, isSelected: selectedPlayerId === p.id, onClick: onClickPlayer, onSwapWithPitchPlayer: onSwapBenchPlayer }, p.id)); }) }))] }));
};
